import cleanserImage from "../../public_html/assets/images/category-cleanse.svg";
import moisturiserImage from "../../public_html/assets/images/category-moisture.svg";
import bodyCareImage from "../../public_html/assets/images/category-body.svg";
import heroImage from "../../public_html/assets/images/hero-beauty.svg";
import { importedProductImages } from "./importedProductImages";
import { importedProductRecords, type ImportedProductRecord } from "./importedProducts";
import { getProductFacts, verifiedIngredientItems } from "./productFacts";

export type Region = "uk" | "ireland";
export type ProductAvailability = "coming-soon" | "available" | "unavailable";
export type WorkflowState = "draft" | "approved" | "published";
export type VisibilityState = "hidden" | "preview" | "published";
export type VatTreatment = "pending" | "standard" | "zero" | "exempt";
export type CheckState = "pending" | "cleared" | "blocked";
export type OrderStatus =
  | "new"
  | "sourcing"
  | "preparing"
  | "dispatched"
  | "delayed"
  | "cancelled"
  | "refunded";

export interface ImageRights {
  owner: string;
  licence: string;
  sourceUrl?: string;
  approvedForCommercialUse: boolean;
}

export interface CatalogueImage {
  src: string;
  alt: string;
  rights: ImageRights;
}

export interface ProductVariant {
  id: string;
  label: string;
  size?: string;
  shade?: string;
  scent?: string;
  packSize?: string;
  supplierSku?: string;
}

export interface BundleComponent {
  supplierSku: string;
  productId?: string;
  name: string;
  quantity: number;
  checkedCostPence?: number | null;
}

export interface DeliveryEstimate {
  uk: string | null;
  ireland: string | null;
}

export interface LaunchChecks {
  imageRights: CheckState;
  ingredientsAndLabels: CheckState;
  productCompliance: CheckState;
  ukEligibility: CheckState;
  irelandEligibility: CheckState;
  landedCost: CheckState;
  taxTreatment: CheckState;
  availability: CheckState;
  delivery: CheckState;
  returns: CheckState;
  electricalSafety: "not_applicable" | "required";
}

export interface Product {
  id: string;
  slug: string;
  name: string;
  brand: string;
  category: string;
  subcategory?: string;
  supplierSku: string;
  auroraId?: string;
  sourceUrl?: string;
  dateChecked?: string;
  ukEligible: boolean;
  irelandEligible: boolean;
  retailPricePence: number | null;
  observedAuroraGbpPence: number | null;
  sourceCostPence: number | null;
  vatTreatment: VatTreatment;
  marginPercent: number | null;
  currency: "GBP";
  variants: ProductVariant[];
  bundleComponents: BundleComponent[];
  likelyBundle: boolean;
  needsElectricalReview: boolean;
  visibility: VisibilityState;
  launchChecks: LaunchChecks;
  images: CatalogueImage[];
  description: string;
  ingredients: string[];
  directions: string | null;
  warnings: string | null;
  packSize: string | null;
  availability: {
    status: ProductAvailability;
    verifiedAt: string | null;
    stockHeldAndVerified: boolean;
  };
  deliveryEstimate: DeliveryEstimate;
  workflowState: WorkflowState;
  relatedProductIds: string[];
}

export interface Category {
  id: string;
  name: string;
  description: string;
  image: string;
  imageAlt: string;
}

export type ProductSort = "name-asc" | "price-asc" | "price-desc" | "newest";
export type PriceFilter = "all" | "under-20" | "20-40" | "40-80" | "80-plus";

const originalIllustrationRights: ImageRights = {
  owner: "Beautify Ourselves",
  licence: "Original illustration",
  approvedForCommercialUse: true
};

const auroraPhotographRights: ImageRights = {
  owner: "Aurora Beautiful",
  licence: "Permission granted to Beautify Ourselves for product photographs",
  approvedForCommercialUse: true
};

export const categories: Category[] = [
  {
    id: "cleansers",
    name: "Cleansers",
    description: "Daily cleansers and cleansing balms listed from the preview catalogue.",
    image: cleanserImage,
    imageAlt: "Abstract cleanser bottle illustration in warm rose tones"
  },
  {
    id: "treatments",
    name: "Treatments",
    description: "Serums, essences and targeted treatments for browsing before launch.",
    image: moisturiserImage,
    imageAlt: "Abstract treatment jar illustration in soft green and cream"
  },
  {
    id: "moisturisers",
    name: "Moisturisers",
    description: "Creams and lotions shown with preview prices only.",
    image: moisturiserImage,
    imageAlt: "Abstract moisturiser jar illustration in soft green and cream"
  },
  {
    id: "masks",
    name: "Masks",
    description: "Face and sheet masks from the preview catalogue.",
    image: moisturiserImage,
    imageAlt: "Abstract mask jar illustration in soft green and cream"
  },
  {
    id: "suncare",
    name: "Suncare",
    description: "Sun serums, creams and sticks listed for preview browsing.",
    image: moisturiserImage,
    imageAlt: "Abstract suncare illustration in soft green and cream"
  },
  {
    id: "makeup",
    name: "Makeup",
    description: "Colour and lip products shown as preview listings.",
    image: cleanserImage,
    imageAlt: "Abstract makeup still-life illustration in warm rose tones"
  },
  {
    id: "hair",
    name: "Hair",
    description: "Hair care listed from the preview catalogue.",
    image: bodyCareImage,
    imageAlt: "Abstract hair-care bottle illustration in warm blush tones"
  },
  {
    id: "body-care",
    name: "Body care",
    description: "Body and hand care shown with preview prices only.",
    image: bodyCareImage,
    imageAlt: "Abstract body-care bottle illustration in warm blush tones"
  },
  {
    id: "fragrance",
    name: "Fragrance",
    description: "Fragrance listings from the preview catalogue.",
    image: heroImage,
    imageAlt: "Abstract still life illustration in warm rose tones"
  },
  {
    id: "sets",
    name: "Sets",
    description: "Existing named sets shown as one listing each. Contents are not listed until verified.",
    image: moisturiserImage,
    imageAlt: "Abstract set illustration in soft green and cream"
  },
  {
    id: "devices",
    name: "Devices",
    description: "Beauty devices held for a separate product-safety review.",
    image: bodyCareImage,
    imageAlt: "Abstract device illustration in warm blush tones"
  },
  {
    id: "other",
    name: "Other",
    description: "Preview listings that do not yet fit a more specific category.",
    image: heroImage,
    imageAlt: "Abstract beauty still life in warm rose tones"
  }
];

export const headerCategoryIds = [
  "cleansers",
  "treatments",
  "moisturisers",
  "hair",
  "makeup"
] as const;

function categoryArtwork(categoryId: string): CatalogueImage {
  const category = categories.find((item) => item.id === categoryId) ?? categories.at(-1)!;
  return {
    src: category.image,
    alt: category.imageAlt,
    rights: originalIllustrationRights
  };
}

function pendingLaunchChecks(
  needsElectricalReview: boolean,
  imageRights: CheckState = "pending"
): LaunchChecks {
  return {
    imageRights,
    ingredientsAndLabels: "pending",
    productCompliance: "pending",
    ukEligibility: "pending",
    irelandEligibility: "pending",
    landedCost: "pending",
    taxTreatment: "pending",
    availability: "pending",
    delivery: "pending",
    returns: "pending",
    electricalSafety: needsElectricalReview ? "required" : "not_applicable"
  };
}

function productPhotographs(record: ImportedProductRecord): CatalogueImage[] {
  const files = importedProductImages[record.auroraId] ?? [];
  if (files.length === 0) return [categoryArtwork(record.category)];

  return files.map((src, index) => ({
    src,
    alt:
      index === 0
        ? record.name
        : `${record.name}, photograph ${index + 1}`,
    rights: auroraPhotographRights
  }));
}

function hydrateImportedProduct(record: ImportedProductRecord): Product {
  const images = productPhotographs(record);
  const hasMatchedPhoto = Boolean(importedProductImages[record.auroraId]?.length);
  const facts = getProductFacts(record.auroraId);
  const launchChecks = pendingLaunchChecks(
    record.needsElectricalReview,
    hasMatchedPhoto ? "cleared" : "pending"
  );
  if (facts?.ingredientsVerified) {
    launchChecks.ingredientsAndLabels = "cleared";
  }

  return {
    id: `aurora-${record.auroraId}`,
    slug: record.slug,
    name: record.name,
    brand: record.brand,
    category: record.category,
    supplierSku: "",
    auroraId: record.auroraId,
    sourceUrl: record.sourceUrl,
    dateChecked: record.dateChecked,
    ukEligible: false,
    irelandEligible: false,
    retailPricePence: record.retailPricePence,
    observedAuroraGbpPence: record.observedAuroraGbpPence,
    sourceCostPence: null,
    vatTreatment: "pending",
    marginPercent: null,
    currency: "GBP",
    variants: [],
    bundleComponents: [],
    likelyBundle: record.likelyBundle,
    needsElectricalReview: record.needsElectricalReview,
    visibility: "preview",
    launchChecks,
    images,
    description: facts?.description?.text ?? "",
    ingredients: verifiedIngredientItems(facts),
    directions: facts?.directions?.text ?? null,
    warnings: facts?.warnings?.text ?? null,
    packSize: record.packSize,
    availability: {
      status: "coming-soon",
      verifiedAt: null,
      stockHeldAndVerified: false
    },
    deliveryEstimate: {
      uk: null,
      ireland: null
    },
    workflowState: "draft",
    relatedProductIds: []
  };
}

export const products: Product[] = importedProductRecords.map(hydrateImportedProduct);

export function isEligibleForRegion(product: Product, region: Region): boolean {
  return region === "uk" ? product.ukEligible : product.irelandEligible;
}

export function isVerifiedInStock(product: Product): boolean {
  return (
    product.availability.status === "available" &&
    product.availability.stockHeldAndVerified &&
    product.availability.verifiedAt !== null
  );
}

export function hasApprovedPrice(product: Product): boolean {
  return (
    product.retailPricePence !== null &&
    product.sourceCostPence !== null &&
    product.marginPercent !== null &&
    product.vatTreatment !== "pending"
  );
}

export function isPublished(product: Product): boolean {
  return (
    product.workflowState === "published" &&
    product.visibility === "published" &&
    hasApprovedPrice(product) &&
    product.images.length > 0 &&
    product.images.every((image) => image.rights.approvedForCommercialUse)
  );
}

export function isPreviewVisible(product: Product): boolean {
  return product.visibility === "preview" && product.retailPricePence !== null;
}

export function publishedProducts(): Product[] {
  return products.filter(isPublished);
}

export function previewProducts(): Product[] {
  return products.filter(isPreviewVisible);
}

export function visibleProducts(): Product[] {
  return products.filter((product) => isPublished(product) || isPreviewVisible(product));
}

export function publishedCategories(): Category[] {
  const used = new Set(publishedProducts().map((product) => categoryId(product)));
  return categories.filter((category) => used.has(category.id));
}

export function visibleCategories(): Category[] {
  const used = new Set(visibleProducts().map((product) => categoryId(product)));
  return categories.filter((category) => used.has(category.id));
}

export function headerCategories(): Category[] {
  const live = new Set(visibleCategories().map((category) => category.id));
  return categories.filter((category) =>
    headerCategoryIds.includes(category.id as (typeof headerCategoryIds)[number]) && live.has(category.id)
  );
}

export function categoryId(product: Product): string {
  return product.category.toLocaleLowerCase().replace(/\s+/g, "-");
}

export function findCategory(id: string | undefined): Category | undefined {
  if (!id) return undefined;
  return categories.find((category) => category.id === id);
}

export function categoryLabel(product: Product): string {
  return findCategory(categoryId(product))?.name ?? product.category;
}

export function isProductPhotograph(image: CatalogueImage | undefined): boolean {
  return image?.rights.owner === "Aurora Beautiful";
}

export function isBundle(product: Product): boolean {
  return product.bundleComponents.length > 0;
}

export function searchProducts(query: string, list = visibleProducts()): Product[] {
  const needle = query.trim().toLocaleLowerCase();
  if (!needle) return list;
  return list.filter((product) => {
    const haystack = [
      product.name,
      product.brand,
      categoryLabel(product),
      product.subcategory ?? "",
      product.packSize ?? "",
      product.auroraId ?? ""
    ]
      .join(" ")
      .toLocaleLowerCase();
    return haystack.includes(needle);
  });
}

export function sortProducts(list: Product[], sort: ProductSort): Product[] {
  return [...list].sort((a, b) => {
    if (sort === "price-asc" || sort === "price-desc") {
      const left = a.retailPricePence ?? Number.POSITIVE_INFINITY;
      const right = b.retailPricePence ?? Number.POSITIVE_INFINITY;
      const priced = sort === "price-asc" ? left - right : right - left;
      return priced || a.name.localeCompare(b.name);
    }
    if (sort === "newest") {
      return (
        (b.dateChecked ?? "").localeCompare(a.dateChecked ?? "") || a.name.localeCompare(b.name)
      );
    }
    return a.name.localeCompare(b.name);
  });
}

export function matchesPriceFilter(product: Product, filter: PriceFilter): boolean {
  const price = product.retailPricePence;
  if (filter === "all" || price === null) return filter === "all" || false;
  if (filter === "under-20") return price < 2000;
  if (filter === "20-40") return price >= 2000 && price < 4000;
  if (filter === "40-80") return price >= 4000 && price < 8000;
  return price >= 8000;
}

export function catalogueBrands(list = visibleProducts()): string[] {
  return [...new Set(list.map((product) => product.brand).filter(Boolean))].sort((left, right) =>
    left.localeCompare(right)
  );
}

export function findVisibleProduct(slugOrId: string | undefined): Product | undefined {
  if (!slugOrId) return undefined;
  return visibleProducts().find(
    (item) => item.slug === slugOrId || item.id === slugOrId || item.auroraId === slugOrId
  );
}

export function productsByAuroraIds(ids: readonly string[]): Product[] {
  const byId = new Map(
    visibleProducts()
      .filter((product) => product.auroraId)
      .map((product) => [product.auroraId as string, product])
  );

  return ids.flatMap((id) => {
    const product = byId.get(id);
    return product ? [product] : [];
  });
}

export function findPublishedProduct(slugOrId: string | undefined): Product | undefined {
  if (!slugOrId) return undefined;
  return publishedProducts().find(
    (item) => item.slug === slugOrId || item.id === slugOrId || item.auroraId === slugOrId
  );
}

export function relatedPreviewProducts(product: Product, limit = 4): Product[] {
  return visibleProducts()
    .filter((item) => item.id !== product.id && categoryId(item) === categoryId(product))
    .slice(0, limit);
}

export function pendingLaunchCheckLabels(product: Product): string[] {
  const labels: Record<keyof Omit<LaunchChecks, "electricalSafety">, string> = {
    imageRights: "image rights",
    ingredientsAndLabels: "ingredients and labels",
    productCompliance: "product compliance",
    ukEligibility: "UK eligibility",
    irelandEligibility: "Ireland eligibility",
    landedCost: "landed cost",
    taxTreatment: "tax treatment",
    availability: "availability",
    delivery: "delivery",
    returns: "returns"
  };

  const pending = (Object.keys(labels) as Array<keyof typeof labels>)
    .filter((key) => product.launchChecks[key] === "pending")
    .map((key) => labels[key]);

  if (product.launchChecks.electricalSafety === "required") {
    pending.push("electrical product-safety review");
  }

  return pending;
}
